import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Careers = () => {
  const [expandedJob, setExpandedJob] = useState(null);

  const jobs = [
    {
      id: 1,
      title: "B2B Selger",
      type: "Heltid / Provisjon",
      location: "Oslo / Remote",
      department: "Salg",
      description: `"Er du en sulten selger som motiveres av gode resultater og høy inntjening? Vi søker deg som vil selge et lovpålagt og etterspurt produkt."\n\nSalesWork CRM er i kraftig vekst, og vi skal nå styrke vårt salgsteam mot bedriftsmarkedet. Vi selger en digital plattform som hjelper bedrifter å selge mer effektivt.\n\n**Dine arbeidsoppgaver:**\n- Oppsøkende salg mot små og mellomstore bedrifter (SMB).\n- Booke og gjennomføre digitale salgsmøter/demoer.\n- Bygge og forvalte din egen portefølje.\n- Forhandle og lukke avtaler.\n\n**Vi ser etter deg som:**\n- Har erfaring med B2B-salg.\n- Er uredd, strukturert og selvgående.\n- Kommuniserer godt på norsk.\n- Motiveres av budsjetter og resultater.\n\n**Vi tilbyr:**\n- Meget gode provisjonsmodeller uten tak.\n- Et ferdigutviklet produkt som er "lett" å selge.\n- Full opplæring og tett oppfølging.`,
      contact: { name: "Thomas Fugleberg", email: "thomas@saleswork.no", phone: "40 55 19 89" }
    },
    { id: 2, title: "Partner (Selvstendig)", type: "Frilans / Selvstendig", location: "Hele Norge", department: "Partnerprogram", description: "Bygg din egen business med SalesWork i ryggen. Vi tilbyr markedets beste partnerprogram." },
    { id: 3, title: "Teamleder Salg", type: "Fast stilling", location: "Oslo", department: "Salgsledelse", description: "Vi søker en operativ leder som kan bygge og motivere vinnerkulturer." },
    { id: 4, title: "Salgssjef", type: "Fast stilling / Management", location: "Oslo", department: "Ledelse", description: "Strategisk ansvar for salg i SalesWork CRM." }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* Header / Nav */}
      <nav className="bg-[#1e293b] py-6 px-8 flex justify-between items-center text-white">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center font-black text-xl">S</div>
          <span className="text-xl font-display font-black">SalesWork CRM</span>
        </Link>
        <div className="flex gap-6 text-sm font-bold">
          <Link to="/" className="hover:text-blue-300 transition-colors">Hjem</Link>
          <Link to="/features" className="hover:text-blue-300 transition-colors">Funksjoner</Link>
          <Link to="/login" className="px-6 py-2 bg-white text-slate-900 rounded-lg hover:bg-blue-50 transition-colors">Logg inn</Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#1e293b] text-white py-24 px-6 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="text-5xl font-black mb-6">Karriere i SalesWork</h1>
          <p className="text-xl text-blue-200 font-medium leading-relaxed">
            Vi bygger Norges tryggeste plattform for salg og vekst. Vi vokser raskt og trenger dyktige folk som vil være med å gjøre en forskjell i arbeidslivet.
          </p>
        </div>
      </div>

      {/* Job List */}
      <div className="max-w-4xl mx-auto px-6 py-20 -mt-10 relative z-20">
        <div className="space-y-4">
          {jobs.map(job => (
            <motion.div 
              key={job.id} 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white text-slate-900 rounded-xl overflow-hidden shadow-lg border border-slate-200"
            >
              <div 
                onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                className="p-8 flex flex-col md:flex-row md:items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-6">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 border border-blue-100">
                    <SafeIcon icon="📈" className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-black text-xl text-slate-900">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500 mt-2 font-bold uppercase tracking-wide">
                      <span className="flex items-center gap-1"><SafeIcon icon="💼" /> {job.type}</span>
                      <span className="flex items-center gap-1"><SafeIcon icon="📍" /> {job.location}</span>
                      <span className="flex items-center gap-1"><SafeIcon icon="👥" /> {job.department}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 text-blue-600 text-sm font-bold flex items-center gap-2 self-end md:self-center">
                  Les mer <SafeIcon icon="▼" className={`transition-transform duration-300 ${expandedJob === job.id ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              <AnimatePresence>
                {expandedJob === job.id && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }} 
                    animate={{ height: 'auto', opacity: 1 }} 
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-slate-100 bg-slate-50/50"
                  >
                    <div className="p-8 md:p-10">
                      <div className="prose prose-slate max-w-none whitespace-pre-wrap leading-relaxed text-slate-700">
                        {job.description}
                      </div>
                      
                      <div className="mt-10 pt-8 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6">
                        {job.contact && (
                          <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm w-full md:w-auto">
                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 font-bold">
                              {job.contact.name.substring(0,2)}
                            </div>
                            <div>
                              <p className="text-[10px] uppercase font-black text-slate-400">Kontaktperson</p>
                              <p className="font-bold text-sm text-slate-900">{job.contact.name}</p>
                              <a href={`mailto:${job.contact.email}`} className="text-xs text-blue-600 hover:underline">{job.contact.email}</a>
                            </div>
                          </div>
                        )}
                        <div className="flex gap-4 w-full md:w-auto">
                          <button className="flex-1 md:flex-none px-8 py-4 bg-blue-600 text-white rounded-xl text-sm font-black uppercase tracking-widest hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all">
                            Søk på stillingen
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 bg-blue-600 text-white p-10 rounded-[2rem] text-center shadow-2xl relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="font-black text-2xl mb-2">Fant du ikke drømmejobben?</h3>
            <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">Vi er alltid på jakt etter talent. Send oss en åpen søknad og fortell hvorfor du hører hjemme hos oss.</p>
            <button className="bg-white text-blue-600 font-black text-sm px-8 py-4 rounded-xl hover:bg-blue-50 transition-colors uppercase tracking-widest">
              Send åpen søknad
            </button>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-12 text-center text-slate-500 text-sm">
        <p>&copy; 2024 SalesWork AS. Alle rettigheter reservert.</p>
      </footer>
    </div>
  );
};

export default Careers;